import React, { useState, useRef, useCallback } from 'react';
import { Upload, Loader2, CheckCircle, AlertTriangle, Camera, ArrowRight, Video } from 'lucide-react';
import { Button } from './ui/button';
import { scoreBucket, toneClass, friendlyUploadError } from '../utils/coachLanguage';
import api from '../services/api';
import { toast } from 'sonner';
import { useVideoMovementAnalyzer } from '../hooks/useVideoMovementAnalyzer';
import { computeMobilityMeasurement } from '../lib/mobilityRom';

const sideLabel = (s) => (s === 'L' ? 'LEFT' : s === 'R' ? 'RIGHT' : 'BOTH SIDES');

/**
 * Upload-based mobility capture (replaces the flaky live auto-runner).
 *
 * For each side the test needs: show the demo + setup, let the user upload a
 * short clip, the SERVER computes a smoothed, held-peak angle + guard, we show
 * the value and let the user Accept or Redo. When every side is accepted we
 * hand the collected measurements up via the SAME onComplete contract the live
 * view used → { test_id, measurements:[{side, raw_value, guard_violated, guard_peak_violation}] }.
 */
const AssessmentUploadView = ({ testDef, onComplete }) => {
  const sides = testDef.sides || ['BILATERAL'];
  const [sideIdx, setSideIdx] = useState(0);
  const [accepted, setAccepted] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const fileRef = useRef(null);
  const { progress: analyzeProgress, analyze, videoRef, canvasRef } = useVideoMovementAnalyzer();
  const currentSide = sides[sideIdx];

  const pickFile = () => fileRef.current?.click();

  const handleFile = useCallback(async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!/^video\//.test(file.type)) { toast.error('Please pick an mp4, mov or webm video.'); return; }
    if (file.size > 100 * 1024 * 1024) { toast.error('Keep clips under 100 MB.'); return; }
    setUploading(true); setError(null); setResult(null);
    let url;
    try {
      // Analyze IN-BROWSER (MediaPipe over the clip) — same engine as live
      // Sessions / form checks. The video never leaves the device; only the
      // small measurement JSON is sent for scoring. No server pose timeout.
      url = URL.createObjectURL(file);
      const v = videoRef.current;
      v.src = url;
      const frames = await analyze(v, { fps: 10 });
      if (!frames || frames.length === 0) throw new Error('no-frames');
      const measurement = computeMobilityMeasurement(frames, testDef, currentSide);
      const res = await api.scoreMobilityMeasurement({
        test_id: testDef.id,
        side: currentSide,
        view_detected: testDef.camera_view || 'side',
        ...measurement,
      });
      setResult(res.data);
    } catch (err) {
      const msg = err?.message === 'no-frames'
        ? "Couldn't see your body in that clip — film your whole body from the side, well lit, ~2-3m back."
        : friendlyUploadError(err, 'Could not analyze that clip. Try a clearer one.');
      setError(msg);
      toast.error(msg);
    } finally {
      setUploading(false);
      if (url) URL.revokeObjectURL(url);
    }
  }, [testDef, currentSide, analyze, videoRef]);

  const acceptResult = () => {
    if (!result) return;
    const measurement = {
      side: currentSide,
      raw_value: result.raw_value,
      guard_violated: result.guard_violated,
      guard_peak_violation: result.guard_peak_violation,
    };
    const next = [...accepted, measurement];
    setResult(null); setError(null);
    if (sideIdx + 1 < sides.length) {
      setAccepted(next);
      setSideIdx(sideIdx + 1);
      toast.success(`${sideLabel(currentSide)} saved — now the ${sideLabel(sides[sideIdx + 1])} side.`);
    } else {
      onComplete({ test_id: testDef.id, measurements: next });
    }
  };

  const bk = scoreBucket(result?.score ?? null);

  return (
    <div className="grid lg:grid-cols-2 gap-5" data-testid="mobility-upload-view">
      {/* LEFT: setup + upload / result */}
      <div className="hpme-card p-5">
        <div className="hpme-card-corner" />
        <div className="flex items-center justify-between mb-1">
          <p className="hpme-label">{testDef.name}</p>
          <span className="text-xs font-mono px-2 py-0.5 rounded uppercase tracking-wider bg-primary/15 text-primary border border-primary/30" data-testid="mobility-side-indicator">
            {sideLabel(currentSide)}{sides.length > 1 ? ` · ${sideIdx + 1}/${sides.length}` : ''}
          </span>
        </div>

        <p className="text-sm text-zinc-200 mt-2 mb-1 font-medium">
          {testDef.side_setup_instruction?.[currentSide] || 'Set up as shown, then record the movement.'}
        </p>
        <div className="flex items-start gap-1.5 mb-4">
          <Camera className="w-3.5 h-3.5 text-primary mt-0.5 shrink-0" />
          <p className="text-xs text-zinc-400">
            Film from the <span className="text-primary font-semibold uppercase">{testDef.camera_view}</span>, whole body in frame, ~2-3m back.
          </p>
        </div>

        <input
          ref={fileRef}
          type="file"
          accept="video/*"
          className="hidden"
          onChange={handleFile}
          data-testid="mobility-upload-input"
        />
        <video ref={videoRef} className="hidden" playsInline muted preload="metadata" />
        <canvas ref={canvasRef} className="hidden" />

        {!result && (
          <button
            type="button"
            onClick={pickFile}
            disabled={uploading}
            className="w-full border-2 border-dashed border-zinc-700 hover:border-primary/60 rounded-sm py-10 flex flex-col items-center justify-center gap-2 transition-colors disabled:opacity-60"
            data-testid="mobility-upload-btn"
          >
            {uploading ? (
              <>
                <Loader2 className="w-8 h-8 text-primary animate-spin" />
                <span className="text-sm text-zinc-300">Analyzing on your device…{analyzeProgress ? ` ${Math.round(analyzeProgress * 100)}%` : ''}</span>
                <span className="text-xs text-zinc-500">your clip stays private — never uploaded</span>
              </>
            ) : (
              <>
                <Upload className="w-8 h-8 text-zinc-500" />
                <span className="text-sm text-zinc-300 font-medium">Upload your {sideLabel(currentSide)} clip</span>
                <span className="text-xs text-zinc-500">5-8 seconds · mp4 or mov</span>
              </>
            )}
          </button>
        )}

        {error && (
          <p className="text-sm text-red-400 mt-3 flex items-start gap-2" data-testid="mobility-error">
            <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" /> {error}
          </p>
        )}

        {result && (
          <div className="mt-1" data-testid="mobility-result">
            <div className="bg-zinc-800/50 rounded-sm p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-green-500" />
                  <span className="text-sm text-zinc-300">{result.primary_label}</span>
                </div>
                <div className="text-right">
                  <span className="font-secondary font-bold text-2xl text-zinc-100" data-testid="mobility-result-value">
                    {Math.round(result.raw_value)}°
                  </span>
                  <span className={`block text-xs font-bold ${toneClass(bk.tone)}`}>
                    {bk.label} · {result.score}/100
                  </span>
                </div>
              </div>

              {result.guard_violated && (
                <p className="text-xs text-yellow-400 mt-3 flex items-start gap-1.5" data-testid="mobility-guard-warning">
                  <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
                  {result.guard_hint || 'Compensation detected — the score was adjusted. Redo for a cleaner read if you like.'}
                </p>
              )}
              {result.low_movement && !result.guard_violated && (
                <p className="text-xs text-yellow-400 mt-3 flex items-start gap-1.5">
                  <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
                  Barely any movement detected — make sure the full motion is in the clip.
                </p>
              )}
            </div>

            <div className="flex gap-3 mt-4">
              <Button onClick={acceptResult} className="hpme-btn-primary flex-1" data-testid="mobility-accept-btn">
                {sideIdx + 1 < sides.length
                  ? <>Looks good → {sideLabel(sides[sideIdx + 1])} <ArrowRight className="w-4 h-4 ml-1" /></>
                  : <>Looks good → save <CheckCircle className="w-4 h-4 ml-1" /></>}
              </Button>
              <Button onClick={() => { setResult(null); setError(null); pickFile(); }} variant="outline" className="flex-1" data-testid="mobility-redo-btn">
                <Upload className="w-4 h-4 mr-1" /> Redo this clip
              </Button>
            </div>
          </div>
        )}

        {accepted.length > 0 && !result && (
          <div className="mt-4 flex flex-wrap gap-2" data-testid="mobility-accepted-chips">
            {accepted.map((m) => (
              <span key={m.side} className="text-xs px-2 py-1 rounded bg-green-500/10 text-green-300 border border-green-500/20">
                {sideLabel(m.side)} ✓ {Math.round(m.raw_value)}°
              </span>
            ))}
          </div>
        )}
      </div>

      {/* RIGHT: how-to (clean text guidance — same style as the Workouts form-check) */}
      <div className="hpme-card p-5">
        <div className="hpme-card-corner" />
        <p className="hpme-label mb-3 flex items-center gap-1.5"><Video className="w-3.5 h-3.5" /> How to record</p>
        <p className="text-sm text-zinc-300 leading-relaxed mb-4">
          Film from the <span className="text-primary font-semibold uppercase">{testDef.camera_view}</span>, your whole body in frame and well lit, standing ~2-3m back. Keep the camera still and capture the full movement, start to finish.
        </p>
        <p className="hpme-label mb-2">Steps</p>
        <ol className="space-y-1.5" data-testid="mobility-howto-steps">
          {(testDef.instructions || []).map((line, i) => (
            <li key={i} className="text-sm text-zinc-300 flex gap-2">
              <span className="text-primary font-mono text-xs mt-0.5">{i + 1}.</span> {line}
            </li>
          ))}
        </ol>
        {testDef.video_query && (
          <a
            href={`https://www.youtube.com/results?search_query=${encodeURIComponent(testDef.video_query)}`}
            target="_blank" rel="noopener noreferrer"
            className="mt-4 inline-flex items-center gap-1.5 text-sm text-red-400 hover:text-red-300"
            data-testid="mobility-upload-tutorial-link"
          >
            <Video className="w-4 h-4" /> Watch video tutorial
          </a>
        )}
      </div>
    </div>
  );
};

export default AssessmentUploadView;
